<template>
  <DialogHeader>
    <template #left>
      <div @click="close">Cancel</div>
    </template>
    Compare more and save
  </DialogHeader>
  <DialogContent>
    <div v-if="loading" class="h-[266px] flex items-center justify-center">
      <Spinner :size="22" />
    </div>

    <template v-else>
      <div class="font-semibold mb-[14px]">
        <span class="text-red-500"> Additional 20% savings</span> by purchasing
        multiple reports
      </div>

      <div
        v-for="(product, index) in products"
        :key="product.id"
        class="w-full border border-black/8 shdw flex items-center justify-between px-4 py-3 rounded-xl relative font-secondary text-gray-800 gap-x-9 cursor-pointer"
        :class="[
          getClass(product.id),
          { 'mb-2.5': index < products.length - 1 },
        ]"
        @click="handleClick(product.id)"
      >
        <div
          v-if="product.credits > 1"
          class="w-fit h-5 bg-red-500 rounded-tr-[10px] rounded-bl-[10px] text-white font-secondary text-[11px] leading-[12px] px-2.5 flex items-center justify-center absolute top-0 right-0 font-medium uppercase"
        >
          Save additional 20%
        </div>

        <div>
          <div class="font-medium text-[14px] leading-[22px]">
            {{ product.name }}
          </div>

          <div class="text-[14px] leading-[22px]">
            {{ product.description }}
          </div>

          <div class="text-[13px] leading-[18px] font-semibold">
            {{ formatProductPrice(product) }}
          </div>
        </div>

        <Checkbox :model-value="selectedProductId === product.id" @click.stop />
      </div>
    </template>

    <UiButton
      class="w-full mt-5"
      primary
      :disabled="!selectedProduct"
      @click="handleContinueClick"
    >
      Continue
    </UiButton>
  </DialogContent>
</template>

<script setup lang="ts">
import { Checkbox, Icon, UiButton } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { computed, ref } from 'vue'
import { graphql } from '@/shared/graphql/router'
import { useQuery } from '@vue/apollo-composable'
import { formatPrice } from '@/shared/utils/money'
import Spinner from '@/shared/components/Spinner.vue'

const { close, open } = useDialog()

const props = defineProps<{
  openCreateReport?: boolean
  onSuccess?: () => boolean | void | Promise<boolean | void>
}>()

const { result, loading } = useQuery(
  graphql(`
    query ReportProducts {
      reportProducts {
        id
        name
        description
        credits
        price {
          amount
          currencyCode
        }
      }
    }
  `),
  null,
  { clientId: 'router' }
)

const products = computed(() => result.value?.reportProducts ?? [])

const selectedProductId = ref<string | null>(null)

const selectedProduct = computed(() => {
  if (!products.value.length) return null

  if (!selectedProductId.value) {
    const bestValue = products.value.reduce((prev, curr) =>
      curr.credits > prev.credits ? curr : prev
    )
    selectedProductId.value = bestValue.id
  }

  return products.value.find((p) => p.id === selectedProductId.value)
})

type Product = NonNullable<typeof selectedProduct.value>

const handleClick = (productId: string) => {
  selectedProductId.value = productId
}

const getClass = (productId: string) => {
  return {
    'border-green-500 bg-green-200/20 outline outline-green-500':
      selectedProductId.value === productId,
    'hover:bg-gray-200/13': selectedProductId.value !== productId,
  }
}

const formatProductPrice = (product: Product) => {
  return formatPrice({
    amount: product.price.amount / 100,
    currency: product.price.currency,
  })
}

const handleContinueClick = () => {
  if (!selectedProduct.value) return

  open(Dialogs.PayCredits, {
    stack: true,
    props: {
      product: selectedProduct.value,
      openCreateReport: props.openCreateReport,
      onSuccess: props.onSuccess,
    },
  })
}
</script>
